import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import stageMetricsData from '../../data/stageMetrics.json';
import { Button } from '../ui/button';
import { ChevronDown, ChevronUp, Loader2, Calendar } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ValueStreamPerformanceChart } from './ValueStreamPerformanceChart';
import { TransformedCapability } from '../businessModel/valueStreamService';
import OrbusStreamNavigator from '../OrbusStreamNavigatorComponent/OrbusStreamNavigator';
import { DataLoader } from '../OrbusStreamNavigatorComponent/lib/DataLoading/DataLoader';
import { getYfilesRenderer } from '../../services/YfilesRendererService';
import { MetricDetailsDialog } from '../businessModel/MetricDetailsDialog';
import { Metric } from '@/types/metrics';
import { MetricsService } from '../../services/MetricsService';
import { convertMetricsFromResponse } from '../../services/metricsConverter';
import { createCapsifiService, ValueStream as CapsifiValueStream } from '../../services/CapsifiService';
import { useSettings } from '@/services/SettingsContext';
import { usePostLoadHealthAlertsPanel } from '@/hooks/use-alerts-panel';
import { useCopilotContext } from '@/contexts/CopilotContext';
import { useAlerts } from '@/contexts/AlertsContext';
import { useMetricCalculations, computeMetricCalculations, computeNodePercentages, MetricCalculationsResult, getMetricCompositeKey } from '@/hooks/useMetricCalculations';
import { TimeSlider } from './TimeSlider';

// Define a simple interface for global metrics
interface GlobalMetrics {
  numberOfOpportunities: number;
  averageSalesValue: number;
  processMeanTime: number;
}

// Define the value stream interface
interface ValueStream {
  id: string;
  label: string;
  data: {
    stages: any[];
    capabilities: TransformedCapability[];
  };
}

type AggregationLevel = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';

interface ValueStreamPerformanceMapProps {
  initialView?: string;
  selectedValueStream: ValueStream | null;
  isChartExpanded?: boolean;
  toggleChartExpanded?: () => void;
  aggregation: AggregationLevel;
}

export function ValueStreamPerformanceMap({
  isChartExpanded = true,
  toggleChartExpanded = () => {},
  aggregation,
}: Omit<ValueStreamPerformanceMapProps, 'selectedValueStream'>) {
  const [calculatedStages, setCalculatedStages] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [globalMetrics, setGlobalMetrics] = useState<GlobalMetrics>({
    numberOfOpportunities: 0,
    averageSalesValue: 0,
    processMeanTime: 0
  });
  
  // State for metrics dialog
  const [isMetricDialogOpen, setIsMetricDialogOpen] = useState<boolean>(false);
  const [selectedCapabilityMetrics, setSelectedCapabilityMetrics] = useState<Metric[]>([]);
  const [selectedPerformanceMetric, setSelectedPerformanceMetric] = useState<string>('Health');
  const [selectedMetricDimensionId, setSelectedMetricDimensionId] = useState<string | undefined>(undefined);
  const [selectedDimensionItemName, setSelectedDimensionItemName] = useState<string | undefined>(undefined);

  // State for current metric, timeseries data, and aggregation (lifted from MetricDetailsDialog)
  const [currentMetric, setCurrentMetric] = useState<Metric | null>(null);
  const [timeseriesData, setTimeseriesData] = useState<any>(null);
  const [isTimeseriesLoading, setIsTimeseriesLoading] = useState(false);

  // State for metrics data
  const [metrics, setMetrics] = useState<Metric[]>([]);
  
  // State for selected value stream
  const [selectedValueStreamId, setSelectedValueStreamId] = useState<string | null>(null);
  const [valueStreams, setValueStreams] = useState<CapsifiValueStream[]>([]);
  
  const autoLoadAttemptedRef = useRef(false);

  // Time slider state: tracks the selected date via the TimeSlider component
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  });

  // Create DataLoader instance - memoize to prevent recreation
  const dataLoader = useMemo(() => new DataLoader(), []);

  const { apiSettings, isConfigured } = useSettings();
  const { setGraphData, setValueStreamName, setMetricsData } = useCopilotContext();
  const { setSearchApi } = useAlerts();
  
  // Search API from OrbusStreamNavigator - use ref to avoid stale closure in callbacks
  const searchApiRef = useRef<{ search: (term: string) => void; zoomStageHeader: (term: string) => void } | null>(null);
  const handleSearchApiReady = useCallback((api: { search: (term: string) => void; zoomStageHeader: (term: string) => void }) => {
    searchApiRef.current = api;
    setSearchApi(api);
  }, [setSearchApi]);
  
  const shouldFetchData = isConfigured;

  // Create CapsifiService instance
  const capsifiService = useMemo(() => 
      createCapsifiService(apiSettings.selectedModelId || ''), 
      [apiSettings.selectedModelId]
    );

  // State to store the raw graph data from API
  const [rawGraphData, setRawGraphData] = useState<any>(null);
  
  // Snapshot of health directly from yFiles graph (stages and capabilities)
  const [healthSnapshot, setHealthSnapshot] = useState<{
    stages: Array<{ id: string; name: string; health: number }>;
    capabilities: Array<{ id: string; name: string; health: number }>;
    timestamp: number;
  } | null>(null);

  // Dimension ID suffixes for different node types
  const DIMENSION_IDS = {
    APPLICATION: '#APPLICATION',
    TEAM: '#ORGANISATIONUNIT',
    PRODUCT: '#PRODUCT',
  };

  // Shared config for application node types
  const applicationConfig = { 
    dimensionId: DIMENSION_IDS.APPLICATION, 
    label: 'application',
    getItemName: (tag: any) => tag.Name || tag.name || tag.ApplicationGroup || '' 
  };

  // Node type configuration for metric filtering
  const NODE_TYPE_CONFIG: Record<string, { 
    dimensionId?: string; 
    label: string;
    getItemName: (tag: any) => string;
  }> = {
    capability: { 
      dimensionId: undefined, 
      label: 'capability',
      getItemName: (tag) => tag.Name || tag.name || '' 
    },
    application_group: applicationConfig,
    application: applicationConfig,
    team: { 
      dimensionId: DIMENSION_IDS.TEAM, 
      label: 'team',
      getItemName: (tag) => tag.Name || tag.name || '' 
    },
    product: { 
      dimensionId: DIMENSION_IDS.PRODUCT, 
      label: 'product',
      getItemName: (tag) => tag.Name || tag.name || '' 
    },
  };

  // Fetch timeseries data immediately
  const fetchTimeseriesData = useCallback(async () => {
    setIsTimeseriesLoading(true);
    try {
      const metricsService = new MetricsService();
      const data = await metricsService.getMetricsTimeseries(aggregation);
      setTimeseriesData(data);
    } catch (error) {
      console.error('Error fetching timeseries data:', error);
      setTimeseriesData(null);
    } finally {
      setIsTimeseriesLoading(false);
    }
  }, [aggregation]);

  // When aggregation changes after initial load, re-fetch timeseries only (no full graph reload)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // Only refetch if graph data is already loaded (i.e. not the initial mount)
    if (rawGraphData) {
      void fetchTimeseriesData();
    }
  }, [aggregation]); // eslint-disable-line react-hooks/exhaustive-deps

  // Extract all unique dimension item names from graph data
  const dimensionItemNames = useMemo(() => {
    const names: Record<string, Set<string>> = {
      '#APPLICATION': new Set<string>(),
      '#ORGANISATIONUNIT': new Set<string>(),
      '#PRODUCT': new Set<string>(),
    };
    if (!rawGraphData) return names;

    rawGraphData.applicationGroups?.forEach((ag: any) => {
      if (ag.ApplicationGroup) names['#APPLICATION'].add(ag.ApplicationGroup);
    });
    rawGraphData.teams?.forEach((t: any) => {
      if (t.team) names['#ORGANISATIONUNIT'].add(t.team);
    });
    rawGraphData.products?.forEach((p: any) => {
      if (p.Name) names['#PRODUCT'].add(p.Name);
    });

    return names;
  }, [rawGraphData]);

  // Pre-compute calculations for ALL metrics × dimension items once timeseries data is available
  // Key: "metricValueKey" for non-dimensional, "metricValueKey::dimensionItemName" for dimensional
  const allMetricCalculations = useMemo(() => {
    const map = new Map<string, MetricCalculationsResult>();
    if (!timeseriesData || metrics.length === 0) return map;

    for (const m of metrics) {
      const dimSuffix = m.metricDimension
        ? Object.keys(dimensionItemNames).find(suffix => m.metricDimension!.endsWith(suffix))
        : undefined;

      const compositeKey = getMetricCompositeKey(m.metricValueKey, m.Id);
      if (dimSuffix) {
        // Dimensional metric — compute once per dimension item name
        // Special case: leads_by_orgunit should only use ORGANISATIONUNIT dimension
        const itemsToProcess = m.metricValueKey === 'leads_by_orgunit'
          ? dimensionItemNames['#ORGANISATIONUNIT']
          : dimensionItemNames[dimSuffix];
        
        for (const itemName of itemsToProcess) {
          map.set(
            `${compositeKey}::${itemName}`,
            computeMetricCalculations({
              metricValueKey: m.metricValueKey,
              operation: m.Operation,
              metricOperation: m.metricOperation,
              target: m.target,
              decreasingTarget: m.decreasingTarget,
              alertingThresholdPercentage: m.alertingThresholdPercentage,
              timeseriesData,
              dimensionItemName: itemName,
              currentDate: selectedDate,
            }),
          );
        }
      } else {
        // Non-dimensional metric — compute once without dimensionItemName
        map.set(
          compositeKey,
          computeMetricCalculations({
            metricValueKey: m.metricValueKey,
            operation: m.Operation,
            metricOperation: m.metricOperation,
            target: m.target,
            decreasingTarget: m.decreasingTarget,
            alertingThresholdPercentage: m.alertingThresholdPercentage,
            timeseriesData,
            currentDate: selectedDate,
          }),
        );
      }
    }

    // Second pass: for leads_by_orgunit, override target with predecessor team's cumulative series
    const leadsMetric = metrics.find(m => m.metricValueKey === 'leads_by_orgunit');
    const DEBUG_TEAMS = ['DACH BDR', 'DACH Account Executives'];
    const dbg = (msg: string, ...args: any[]) => console.debug(`[leads_by_orgunit DEBUG] ${msg}`, ...args);

    if (!leadsMetric) {
      dbg('No leads_by_orgunit metric found in metrics array. metricValueKeys:', metrics.map(m => m.metricValueKey));
    }

    if (leadsMetric && rawGraphData?.leafs && rawGraphData?.flows && rawGraphData?.stages) {
      const leadsCompositeKey = getMetricCompositeKey(leadsMetric.metricValueKey, leadsMetric.Id);
      dbg('leadsMetric found. Id=%s, compositeKey=%s, operation=%s, metricOperation=%s', leadsMetric.Id, leadsCompositeKey, leadsMetric.Operation, leadsMetric.metricOperation);
      dbg('leadsMetric.target:', leadsMetric.target);

      // Log what the first pass stored for our debug teams
      for (const team of DEBUG_TEAMS) {
        const key = `${leadsCompositeKey}::${team}`;
        const calc = map.get(key);
        dbg('First pass result for "%s" (key="%s"): hasChartData=%s, chartData.length=%d, cumulativeValue=%s, cumulativeSeries.length=%d',
          team, key, calc?.hasChartData, calc?.chartData.length ?? 0, calc?.cumulativeValue, calc?.cumulativeSeries.length ?? 0);
        if (calc?.cumulativeSeries.length) {
          dbg('  cumulativeSeries first 3:', calc.cumulativeSeries.slice(0, 3));
          dbg('  cumulativeSeries last 3:', calc.cumulativeSeries.slice(-3));
        }
      }

      // Log all map keys containing "leads_by_orgunit" to see what was actually stored
      const leadsKeys = [...map.keys()].filter(k => k.includes('leads_by_orgunit'));
      dbg('All map keys containing "leads_by_orgunit" (%d total):', leadsKeys.length, leadsKeys);

      // Build stage order index so we can determine flow direction
      const stageOrder = new Map<string, number>();
      rawGraphData.stages.forEach((s: any, idx: number) => {
        stageOrder.set(s.Name, idx);
      });
      dbg('stageOrder:', Object.fromEntries(stageOrder));

      // Build leaf name → all LeafNode entries with Products for path disambiguation
      const leafsByName = new Map<string, Array<{ Team: string; Stage: string; Capability: string; Products: string[] }>>();
      for (const leaf of rawGraphData.leafs) {
        if (!leaf.Leaf || !leaf.Team) continue;
        if (!leafsByName.has(leaf.Leaf)) leafsByName.set(leaf.Leaf, []);
        leafsByName.get(leaf.Leaf)!.push({
          Team: leaf.Team, Stage: leaf.Stage, Capability: leaf.Capability || '',
          Products: leaf.Products || [],
        });
      }

      // Log leafs relevant to our debug teams
      for (const team of DEBUG_TEAMS) {
        const teamLeafs = rawGraphData.leafs.filter((l: any) => l.Team === team);
        dbg('Leafs for team "%s" (%d):', team, teamLeafs.length, teamLeafs.map((l: any) => ({
          Leaf: l.Leaf, Stage: l.Stage, Capability: l.Capability, Team: l.Team, Products: l.Products
        })));
      }

      // Derive predecessor team for each team using stage ordering + product path.
      // Only pair source/target leaf instances that share at least one product.
      const predecessorTeam = new Map<string, string>();
      for (const flow of rawGraphData.flows) {
        const srcLeafs = leafsByName.get(flow.SourceLeaf) ?? [];
        const tgtLeafs = leafsByName.get(flow.TargetLeaf) ?? [];
        for (const srcLeaf of srcLeafs) {
          for (const tgtLeaf of tgtLeafs) {
            const srcStageIdx = stageOrder.get(srcLeaf.Stage) ?? -1;
            const tgtStageIdx = stageOrder.get(tgtLeaf.Stage) ?? -1;
            const sharedProducts = srcLeaf.Products.filter(p => tgtLeaf.Products.includes(p));

            // Log every evaluation involving our debug teams
            if (DEBUG_TEAMS.includes(srcLeaf.Team) || DEBUG_TEAMS.includes(tgtLeaf.Team)) {
              dbg('Evaluating flow "%s"->"%s": src=[team=%s, stage=%s(idx=%d), products=%o] tgt=[team=%s, stage=%s(idx=%d), products=%o] | sharedProducts=%o forward=%s diffTeams=%s alreadyHasPred=%s',
                flow.SourceLeaf, flow.TargetLeaf,
                srcLeaf.Team, srcLeaf.Stage, srcStageIdx, srcLeaf.Products,
                tgtLeaf.Team, tgtLeaf.Stage, tgtStageIdx, tgtLeaf.Products,
                sharedProducts, srcStageIdx < tgtStageIdx, srcLeaf.Team !== tgtLeaf.Team, predecessorTeam.has(tgtLeaf.Team));
            }

            // Only consider flows going forward, different teams, and sharing a product path
            if (sharedProducts.length > 0
                && srcStageIdx >= 0 && tgtStageIdx >= 0 && srcStageIdx < tgtStageIdx
                && srcLeaf.Team !== tgtLeaf.Team && !predecessorTeam.has(tgtLeaf.Team)) {
              predecessorTeam.set(tgtLeaf.Team, srcLeaf.Team);
              if (DEBUG_TEAMS.includes(srcLeaf.Team) || DEBUG_TEAMS.includes(tgtLeaf.Team)) {
                dbg('>>> PREDECESSOR SET: "%s" -> "%s" (via flow "%s"->"%s", stages %s->%s, sharedProducts=%o)',
                  tgtLeaf.Team, srcLeaf.Team,
                  flow.SourceLeaf, flow.TargetLeaf,
                  srcLeaf.Stage, tgtLeaf.Stage,
                  sharedProducts);
              }
            }
          }
        }
      }
      dbg('Final predecessorTeam map:', Object.fromEntries(predecessorTeam));

      // Override target for each team that has a predecessor
      for (const [teamName, predName] of predecessorTeam) {
        const predKey = `${leadsCompositeKey}::${predName}`;
        const predCalc = map.get(predKey);

        if (DEBUG_TEAMS.includes(teamName) || DEBUG_TEAMS.includes(predName)) {
          dbg('Override attempt: team="%s" predecessor="%s" predKey="%s" predCalcExists=%s predCumulativeSeriesLen=%d',
            teamName, predName, predKey, !!predCalc, predCalc?.cumulativeSeries.length ?? 0);
          if (predCalc?.cumulativeSeries.length) {
            dbg('  predecessor cumulativeSeries first 3:', predCalc.cumulativeSeries.slice(0, 3));
            dbg('  predecessor cumulativeSeries last 3:', predCalc.cumulativeSeries.slice(-3));
          }
        }

        if (!predCalc || predCalc.cumulativeSeries.length === 0) continue;

        const overrideKey = `${leadsCompositeKey}::${teamName}`;
        const overrideResult = computeMetricCalculations({
          metricValueKey: 'leads_by_orgunit',
          operation: leadsMetric.Operation,
          metricOperation: leadsMetric.metricOperation,
          target: predCalc.cumulativeSeries,
          decreasingTarget: leadsMetric.decreasingTarget,
          timeseriesData,
          dimensionItemName: teamName,
          currentDate: selectedDate,
        });
        map.set(overrideKey, overrideResult);

        if (DEBUG_TEAMS.includes(teamName)) {
          dbg('Override DONE for "%s": key="%s" newTargetProgress=%o newCumulativeValue=%s',
            teamName, overrideKey, overrideResult.targetProgress, overrideResult.cumulativeValue);
        }
      }
    } else if (leadsMetric) {
      dbg('Skipping second pass. rawGraphData available: leafs=%s flows=%s stages=%s',
        !!rawGraphData?.leafs, !!rawGraphData?.flows, !!rawGraphData?.stages);
    }

    return map;
  }, [metrics, timeseriesData, dimensionItemNames, selectedDate, rawGraphData]);

  // For the dialog: if current metric is leads_by_orgunit, find predecessor's cumulative series as target
  // Re-uses the stage-aware predecessor logic from allMetricCalculations
  const dialogTarget = useMemo(() => {
    const dbgDialog = (msg: string, ...args: any[]) => console.debug(`[leads_by_orgunit dialogTarget] ${msg}`, ...args);

    if (
      currentMetric?.metricValueKey !== 'leads_by_orgunit' ||
      !selectedDimensionItemName ||
      !rawGraphData?.leafs ||
      !rawGraphData?.flows ||
      !rawGraphData?.stages
    ) {
      if (currentMetric?.metricValueKey === 'leads_by_orgunit') {
        dbgDialog('Early return: selectedDimensionItemName=%s, leafs=%s, flows=%s, stages=%s',
          selectedDimensionItemName, !!rawGraphData?.leafs, !!rawGraphData?.flows, !!rawGraphData?.stages);
      }
      return currentMetric?.target;
    }

    dbgDialog('Computing for dimensionItem="%s"', selectedDimensionItemName);
    const leadsCompositeKey = getMetricCompositeKey(currentMetric.metricValueKey, currentMetric.Id);
    dbgDialog('leadsCompositeKey="%s"', leadsCompositeKey);

    // Build stage order index
    const stageOrder = new Map<string, number>();
    rawGraphData.stages.forEach((s: any, idx: number) => {
      stageOrder.set(s.Name, idx);
    });

    // Build leaf name → all entries with Products for path disambiguation
    const leafsByName = new Map<string, Array<{ Team: string; Stage: string; Capability: string; Products: string[] }>>();
    for (const leaf of rawGraphData.leafs) {
      if (!leaf.Leaf || !leaf.Team) continue;
      if (!leafsByName.has(leaf.Leaf)) leafsByName.set(leaf.Leaf, []);
      leafsByName.get(leaf.Leaf)!.push({
        Team: leaf.Team, Stage: leaf.Stage, Capability: leaf.Capability || '',
        Products: leaf.Products || [],
      });
    }

    // Find predecessor for selectedDimensionItemName using stage ordering + product path
    let flowsChecked = 0;
    let matchesFound = 0;
    for (const flow of rawGraphData.flows) {
      const srcLeafs = leafsByName.get(flow.SourceLeaf) ?? [];
      const tgtLeafs = leafsByName.get(flow.TargetLeaf) ?? [];
      for (const srcLeaf of srcLeafs) {
        for (const tgtLeaf of tgtLeafs) {
          if (tgtLeaf.Team !== selectedDimensionItemName) continue;
          flowsChecked++;
          const srcStageIdx = stageOrder.get(srcLeaf.Stage) ?? -1;
          const tgtStageIdx = stageOrder.get(tgtLeaf.Stage) ?? -1;
          const sharedProducts = srcLeaf.Products.filter(p => tgtLeaf.Products.includes(p));
          dbgDialog('Flow "%s"->"%s": src=[team=%s, stage=%s(idx=%d), products=%o] tgt=[team=%s, stage=%s(idx=%d), products=%o] sharedProducts=%o',
            flow.SourceLeaf, flow.TargetLeaf,
            srcLeaf.Team, srcLeaf.Stage, srcStageIdx, srcLeaf.Products,
            tgtLeaf.Team, tgtLeaf.Stage, tgtStageIdx, tgtLeaf.Products,
            sharedProducts);
          if (sharedProducts.length > 0
              && srcStageIdx >= 0 && tgtStageIdx >= 0 && srcStageIdx < tgtStageIdx
              && srcLeaf.Team !== tgtLeaf.Team) {
            matchesFound++;
            const predKey = `${leadsCompositeKey}::${srcLeaf.Team}`;
            const predCalc = allMetricCalculations.get(predKey);
            dbgDialog('>>> Predecessor match: team="%s", stages %s->%s, sharedProducts=%o, predKey="%s" predCalcExists=%s cumulativeSeriesLen=%d',
              srcLeaf.Team, srcLeaf.Stage, tgtLeaf.Stage,
              sharedProducts, predKey, !!predCalc, predCalc?.cumulativeSeries.length ?? 0);
            if (predCalc && predCalc.cumulativeSeries.length > 0) {
              dbgDialog('RETURNING predecessor cumulativeSeries (%d entries), first 3:', predCalc.cumulativeSeries.length, predCalc.cumulativeSeries.slice(0, 3));
              return predCalc.cumulativeSeries;
            }
          }
        }
      }
    }
    dbgDialog('No predecessor found. flowsChecked=%d, matchesFound=%d. Falling back to currentMetric.target:', flowsChecked, matchesFound, currentMetric?.target);
    return currentMetric?.target;
  }, [currentMetric, selectedDimensionItemName, rawGraphData, allMetricCalculations]);

  // Instantiate metric calculations hook for the current metric (dialog chart)
  const metricCalculations = useMetricCalculations({
    metricValueKey: currentMetric?.metricValueKey ?? '',
    operation: currentMetric?.Operation ?? '',
    metricOperation: currentMetric?.metricOperation,
    target: dialogTarget,
    decreasingTarget: currentMetric?.decreasingTarget,
    timeseriesData,
    dimensionItemName: selectedDimensionItemName,
    currentDate: selectedDate,
  });

  const nodePercentages = useMemo(
    () => computeNodePercentages(metrics, allMetricCalculations, dimensionItemNames, rawGraphData),
    [metrics, allMetricCalculations, dimensionItemNames, rawGraphData],
  );

  // Enrich graph data with real PercentageMetrics from metric calculations
  const memoizedGraphData = useMemo(() => {
    if (!rawGraphData) return null;
    if (nodePercentages.size === 0) return rawGraphData;

    return {
      ...rawGraphData,
      capabilities: rawGraphData.capabilities?.map((cap: any) => {
        const pct = nodePercentages.get(cap.Name);
        return pct ? { ...cap, Percentage: pct } : cap;
      }),
      applicationGroups: rawGraphData.applicationGroups?.map((ag: any) => {
        const pct = nodePercentages.get(ag.ApplicationGroup);
        return pct ? { ...ag, Percentage: pct } : ag;
      }),
      teams: rawGraphData.teams?.map((t: any) => {
        const pct = nodePercentages.get(t.team);
        return pct ? { ...t, Percentage: pct } : t;
      }),
    };
  }, [rawGraphData, nodePercentages]);

  // Handle single click on items in OrbusStreamNavigator
  const onItemSingleClick = useCallback((item: any) => {
    // ... (rest of the code remains the same)
    if (!item?.tag?.type) return;
    
    const tag = item.tag;
    const nodeType = tag.type.toLowerCase();
    const config = NODE_TYPE_CONFIG[nodeType];
    
    if (!config) return;
    
    const itemName = config.getItemName(tag);
    if (!itemName) {
      // No name found in tag
      return;
    }
    
    // Filter metrics based on node type
    let filteredMetrics: Metric[];
    if (config.dimensionId) {
      // Dimensional filtering (application, team, product)
      filteredMetrics = metrics.filter(m => m.metricDimension?.endsWith(config.dimensionId!));
    } else {
      // Capability filtering by name
      filteredMetrics = metrics.filter(m => 
        (m["Business Capability"] || '').toLowerCase() === itemName.toLowerCase()
      );
    }
    
    // Found metrics for item
    
    if (filteredMetrics.length > 0) {
      setSelectedCapabilityMetrics(filteredMetrics);
      setSelectedMetricDimensionId(config.dimensionId);
      setSelectedDimensionItemName(config.dimensionId ? itemName : undefined);
      setIsMetricDialogOpen(true);
    }
  }, [metrics]);

  // Load metrics data using MetricsService
  const loadMetricsData = useCallback(async (graphData: any, valueStreamId: string) => {
    if (!graphData) {
      // No graph data provided
      return;
    }
    
    // Loading metrics data
    
    try {
      // Create MetricsService instance
      const metricsService = new MetricsService();
      
      // Fetch raw metrics data with value stream ID
      const rawMetricsData = await metricsService.getCapsifiVsMetricsRaw({ itemId: valueStreamId });
      // Raw metrics data fetched
      
      // Check if capabilities exist in graph data
      if (!graphData.capabilities || !Array.isArray(graphData.capabilities)) {
        console.error('Graph data has no capabilities array:', graphData);
        return;
      }
      
      // Convert metrics using graphData capabilities
      const convertedMetrics = convertMetricsFromResponse(rawMetricsData, graphData.capabilities);
      
      // Update metrics state
      setMetrics(convertedMetrics);
      
      // Update copilot context with metrics data
      setMetricsData(convertedMetrics);
    } catch (err) {
      console.error('Error loading metrics data:', err);
      setMetrics([]);
      setMetricsData(null);
    }
  }, [setMetricsData]);

  // Fetch value streams list
  const fetchValueStreams = useCallback(async () => {
    try {
      const data = await capsifiService.getValueStreams();

      setValueStreams(data);
      
      // If we have value streams and none is selected, select the first one
      if (data.length > 0) {
        setSelectedValueStreamId((prev) => {
          if (prev) return prev;
          // Selected value stream ID
          return data[0].id;
        });
      }
      
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch value streams list';
      console.error('Error fetching value streams list:', errorMessage);
      return [];
    }
  }, [capsifiService]);

  // Transform graph data to stages format and include metrics from API
  const transformGraphDataToStages = useCallback((graphData: any, snapshot: typeof healthSnapshot, metricCalcs: Map<string, MetricCalculationsResult>): any[] => {
    if (!graphData || !graphData.stages) return [];
    
    return graphData.stages
      // Skip the 'Products' stage
      .filter((stage: any) => stage.Name !== 'Products')
      .map((stage: any, index: number) => {
      // Find metrics related to this stage
      const stageMetrics = metrics.filter(metric => metric["ValueStream Stage"] === stage.Name);
      
      // Calculate metrics for this stage
      const numberOfLeads = stageMetrics.length;
      const closedLeads = stageMetrics.filter(metric => 
        metric.target && metric.baseline && Number(metric.target) <= Number(metric.baseline)
      ).length;
      
      // Calculate average deal size (using targetFinancialBenefit as a proxy)
      const financialBenefits = stageMetrics
        .map(metric => metric.targetFinancialBenefit ? Number(metric.targetFinancialBenefit) : 0)
        .filter(value => !isNaN(value) && value > 0);
      
      const averageDealSize = financialBenefits.length > 0 
        ? financialBenefits.reduce((sum, value) => sum + value, 0) / financialBenefits.length 
        : 0;
      
      // Get stage ID in lowercase format
      const stageId = stage.Name.toLowerCase().replace(/\s+/g, '-');
      
      // Use navigator health snapshot as the canonical health percentage
      // (bottom-up rollup: leaf -> capability -> stage via PercentageMetrics.HealthPercentage)
      const snapshotStage = snapshot?.stages.find(s => s.id === stage.Name);
      const calculatedHealthPercentage: number | undefined = snapshotStage?.health;

      const stageMetricsFromJson = stageMetricsData.stages[index] || {
        baselineValue: 0,
        currentValue: 0,
        healthPercentage: 0
      };
      
      return {
        id: stageId,
        name: stage.Name,
        description: stage.Description || '',
        type: 'value',
        metrics: {
          numberOfLeads,
          closedLeads,
          averageDealSize,
          baselineValue: stageMetricsFromJson.baselineValue,
          currentValue: stageMetricsFromJson.currentValue,
          // Use calculated health percentage if available, otherwise use JSON fallback
          healthPercentage: calculatedHealthPercentage ?? stageMetricsFromJson.healthPercentage,
          // Add other metrics as needed
        },
        // Include original stage data
        ...stage
      };
    });
  }, [metrics, dimensionItemNames]);

  // Load data when component mounts
  const loadData = useCallback(async (valueStreamIdOverride?: string) => {
    setIsLoading(true);
    setHealthSnapshot(null);

    try {
      // First fetch value streams if we don't have any
      let valueStreamId = valueStreamIdOverride ?? selectedValueStreamId;
      if (!valueStreamId) {
        const streams = await fetchValueStreams();
        if (streams.length > 0) {
          valueStreamId = streams[0].id;
        } else {
          throw new Error('No value streams available');
        }
      }
      
      // Check if loadPocData flag is set in session storage
      const loadPocData = sessionStorage.getItem('loadPocData') === 'true';
      
      // Load data based on the flag - either from POC data or API
      const graphData = loadPocData 
        ? await dataLoader.loadPocData() 
        : await dataLoader.loadDataFromApi(capsifiService, valueStreamId);

      // Store the raw graph data for use in OrbusStreamNavigator and transforms
      setRawGraphData(graphData);
      
      // Update copilot context with graph data
      setGraphData(graphData);
      
      // Update value stream name in copilot context
      const vsName = valueStreams.find(vs => vs.id === valueStreamId)?.label;
      setValueStreamName(vsName);
      
      // Transform the graph data to stages format expected by ValueStreamPerformanceMap
      // Initially no snapshot available, pass empty map for metric calculations
      const stages = transformGraphDataToStages(graphData, null, new Map());
      
      setCalculatedStages(stages);
      
      // Calculate global metrics from the first stage
      if (stages.length > 0 && stages[0].metrics) {
        setGlobalMetrics({
          numberOfOpportunities: stages[0].metrics.numberOfLeads || 0,
          averageSalesValue: stages[0].metrics.averageDealSize || 0,
          processMeanTime: 154  // Default value, can be updated with actual data if available
        });
      }
      
      // Load metrics data after graph data is loaded
      await loadMetricsData(graphData, valueStreamId);

      // Fetch timeseries after graph + metrics are ready so all dependencies exist
      await fetchTimeseriesData();
    } catch (err) {
      console.error('Error loading value stream data:', err);
      setCalculatedStages([]);
    } finally {
      setIsLoading(false);
    }
  }, [loadMetricsData, fetchTimeseriesData, fetchValueStreams, selectedValueStreamId, capsifiService, dataLoader, transformGraphDataToStages, setGraphData, setValueStreamName, valueStreams]);

  const handleValueStreamChange = useCallback((value: string) => {
    setIsMetricDialogOpen(false);
    setSelectedCapabilityMetrics([]);
    setSelectedValueStreamId(value);
    void loadData(value);
  }, [loadData]);

  useEffect(() => {
    if (!shouldFetchData) {
      autoLoadAttemptedRef.current = false;
      return;
    }
  }, [shouldFetchData]);

  useEffect(() => {
    if (!shouldFetchData) return;
    if (autoLoadAttemptedRef.current) return;
    autoLoadAttemptedRef.current = true;
    void loadData();
  }, [shouldFetchData, loadData]);

  // Recalculate stage metrics when metrics data or health snapshot changes
  useEffect(() => {
    if (rawGraphData) {
      const stages = transformGraphDataToStages(rawGraphData, healthSnapshot, allMetricCalculations);
      setCalculatedStages(stages);
      
      // Update global metrics from the first stage
      if (stages.length > 0 && stages[0].metrics) {
        setGlobalMetrics({
          numberOfOpportunities: stages[0].metrics.numberOfLeads || 0,
          averageSalesValue: stages[0].metrics.averageDealSize || 0,
          processMeanTime: 154  // Default value, can be updated with actual data if available
        });
      }
    }
  }, [metrics, rawGraphData, healthSnapshot, transformGraphDataToStages, allMetricCalculations]);

  // Post-load health alerts - fires once when data is ready
  usePostLoadHealthAlertsPanel<{ id: string; name: string; health: number }, { id: string; name: string; health: number }>(
    healthSnapshot?.stages,
    {
      getId: (s) => s.id,
      getName: (s) => s.name,
      getHealth: (s) => s.health,
    },
    healthSnapshot?.capabilities,
    {
      getId: (c) => c.id,
      getName: (c) => c.name,
      getHealth: (c) => c.health,
    },
    !isLoading && !!healthSnapshot, // isDataReady
    {
      threshold: 75,
      stageTitle: 'Stage health alert',
      capabilityTitle: 'Capability health alert',
      maxItems: 12,
    }
  );

  // Render loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="mt-2 text-sm text-muted-foreground">Loading value stream data...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-grow min-h-0 gap-1">
      <React.Fragment>
        <div className="flex-shrink-0 pb-1 flex items-center justify-between">
          <Select
            value={selectedValueStreamId || undefined}
            onValueChange={handleValueStreamChange}
          >
            <SelectTrigger className="w-[300px]">
              <SelectValue placeholder="Select a value stream" />
            </SelectTrigger>
            <SelectContent>
              {valueStreams.map((vs) => (
                <SelectItem key={vs.id} value={vs.id}>
                  {vs.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <TimeSlider aggregation={aggregation} onDateChange={setSelectedDate} />
        </div>
        {/* Performance Chart Section */}
        <div className="border rounded-lg bg-white">
          <div 
            className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-gray-50"
            onClick={toggleChartExpanded}
          >
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Value Stream Performance Chart</h2>
              <p className="text-xs text-slate-500">
                Visualize value metrics and stage performance trends
              </p>
            </div>
            <Button variant="ghost" size="sm">
              {isChartExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
          {isChartExpanded && (
            <div className="p-0">
              <ValueStreamPerformanceChart 
                stages={calculatedStages} 
                globalMetrics={globalMetrics}
              />
            </div>
          )}
        </div>

        {/* Value Stream Map */}
        <div className="overflow-hidden relative border rounded-lg flex flex-col" style={{boxShadow: '0px -3px 0px 0px rgba(66, 68, 90, 0.5)', height: isChartExpanded ? '53vh' : '76vh'}}>
          {memoizedGraphData && (
            <OrbusStreamNavigator 
              insetLeft={0}
              insetTop={0}
              insetRight={0}
              insetBottom={-2300}
              graphData={memoizedGraphData}
              onItemSingleClick={onItemSingleClick}
              onSearchApiReady={handleSearchApiReady}
              onHealthSnapshot={(payload) => setHealthSnapshot(payload)}
              onPerformanceMetricChange={setSelectedPerformanceMetric}
              portalTargetId="portal-root"
              renderer={getYfilesRenderer()}
            />
          )}
        </div>
      </React.Fragment>
      
      {/* Metrics Dialog */}
      <MetricDetailsDialog 
        isOpen={isMetricDialogOpen} 
        capabilityMetrics={selectedCapabilityMetrics}
        selectedPerformanceMetric={selectedPerformanceMetric}
        metricDimensionId={selectedMetricDimensionId}
        dimensionItemName={selectedDimensionItemName}
        onClose={() => setIsMetricDialogOpen(false)}
        timeseriesData={timeseriesData}
        isLoading={isTimeseriesLoading}
        aggregation={aggregation}
        currentMetric={currentMetric}
        onCurrentMetricChange={setCurrentMetric}
        calculations={currentMetric ? metricCalculations : null}
        effectiveTarget={dialogTarget}
      />
    </div>
  );
}